/**
 * PATH: packages/email-hub/src/feature/components/BillDocDrawer.tsx
 * FILE_ID: EMAILHUB-BILLDOCDRAWER-001
 * PURPOSE: Right-side slide-in drawer that renders the source INVOICE
 *          document (the bill PDF / image) of an email inline, beside the
 *          reader, reusing the in-package `AttachmentPreviewBody`.
 * WHY:     Operator (2026-06-15) — clicking the "Bill" chip on an email
 *          carrying a subcontractor/supplier invoice should pop the
 *          invoice up in the right drawer (split-screen), not just toast
 *          "opening bill…" with nothing opening.
 * FLOW:
 *   ┌──────────────────┐   ┌────────────────────────┐   ┌────────────────────┐
 *   │ email.attachments │ → │ pickInvoiceAttachment  │ → │ AttachmentPreviewBody│
 *   └──────────────────┘   └────────────────────────┘   └────────────────────┘
 * RELATED:
 *   - ./AttachmentPreviewModal : AttachmentPreviewBody (the inline renderer)
 *   - ./EmailPreviewPane       : mounts this, mirrors CardPeekDrawer
 *   - ./SmartActionBar         : the "Bill" chip that opens it
 */

import { X } from 'lucide-react'

import { cn } from '../../_internal/lib/utils'
import type { Email, EmailAttachment } from '../hooks/useEmails'
import { AttachmentPreviewBody } from './AttachmentPreviewModal'

/**
 * Pure resolver: pick the invoice document off an email's attachments.
 *
 * Order of preference: first PDF (the invoice is virtually always a PDF) →
 * first downloadable → first attachment. Inline images (signatures, logos)
 * are excluded so we never preview a logo as "the bill". Returns ``null``
 * when there's nothing to show.
 */
export function pickInvoiceAttachment(
  attachments: EmailAttachment[] | undefined | null,
): EmailAttachment | null {
  const list = (attachments ?? []).filter((a) => !a.is_inline)
  if (list.length === 0) return null
  const isPdf = (a: EmailAttachment): boolean =>
    a.is_pdf === true ||
    (a.mime_type || a.content_type || '').toLowerCase().includes('pdf') ||
    (a.extension || a.filename || '').toLowerCase().endsWith('.pdf')
  return list.find(isPdf) ?? list.find((a) => !!a.download_url) ?? list[0]
}

export interface BillDocDrawerProps {
  email: Email
  onClose: () => void
}

/**
 * Slide-in drawer pinned to the right edge (mirrors CardPeekDrawer), so the
 * invoice opens "in place" beside the open email without leaving the inbox.
 */
export function BillDocDrawer({ email, onClose }: BillDocDrawerProps) {
  const attachment = pickInvoiceAttachment(email.attachments)

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-black/30"
        onClick={onClose}
        aria-hidden
      />
      <div
        className={cn(
          'fixed inset-y-0 right-0 z-50 flex w-full max-w-[560px] flex-col',
          'border-l border-border bg-card shadow-xl',
        )}
        role="dialog"
        aria-label="Invoice document"
        data-testid="bill-doc-drawer"
      >
        <div className="flex items-center justify-between gap-2 border-b border-border px-4 py-3 shrink-0">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-foreground">Bill - invoice</p>
            <p
              className="text-xs text-muted-foreground truncate"
              title={email.subject || ''}
            >
              {attachment?.filename || email.subject || '(no subject)'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close invoice preview"
            className="rounded-md p-1.5 text-muted-foreground hover:bg-accent shrink-0"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="flex-1 min-h-0">
          {attachment ? (
            <AttachmentPreviewBody attachment={attachment} emailId={email.id} />
          ) : (
            <div className="flex h-full items-center justify-center p-6 text-center text-sm text-muted-foreground">
              No invoice document is attached to this email.
            </div>
          )}
        </div>
      </div>
    </>
  )
}
